import { getConfig } from '@config';
import { CustomThemeMobileBackgroundMode, DesignLayoutType } from '@graphql/generated-base';
import type { CustomThemeSlotsFragment } from '@graphql/fragments/customTheme.graphql.generated';
import type { TemplateArtPreviewIds, TemplateCustomArt } from './websiteTemplates.types';

// The media_service container design_service serves template art from.
const TEMPLATE_ART_CONTAINER = 'defaultarts';
// A bare file name. Rejecting paths and dot-only names keeps a crafted link inside the container.
const ASSET_ID_PATTERN = /^\w[\w.-]*$/;
const PREVIEW_THEME_ID = 'template-art-preview';

const isAssetId = (value: unknown): value is string => typeof value === 'string' && ASSET_ID_PATTERN.test(value);

// The environment's media_service host, which design_service also builds applied art URLs on. Absolute,
// because `addRendition` only resizes http(s) URLs and the local dev server doesn't serve `/media`.
const getMediaServiceBase = (): string => (getConfig().environment === 'dev' ? 'https://dev.withjoy.com/media' : 'https://withjoy.com/media');

/** The media_service URL of a template art image, on the same host design_service uses once applied. */
export const getTemplateArtUrl = (assetId: string): string => `${getMediaServiceBase()}/${TEMPLATE_ART_CONTAINER}/${encodeURIComponent(assetId)}`;

/** The asset ids a preview link carries for a template's art; undefined for a template without art. */
export const toTemplateArtPreviewIds = (customArt: TemplateCustomArt | undefined): TemplateArtPreviewIds | undefined =>
  customArt && {
    desktopBackground: customArt.desktopBackground.assetId,
    ...(customArt.mobileBackground && { mobileBackground: customArt.mobileBackground.assetId }),
    ...(customArt.mobileBanner && { mobileBanner: customArt.mobileBanner.assetId }),
  };

/**
 * Reads preview art ids decoded from a URL. Anything malformed (a missing desktop image, a path or
 * URL in place of an id) drops the whole preview art rather than half of it.
 */
export const parseTemplateArtPreviewIds = (value: unknown): TemplateArtPreviewIds | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  const { desktopBackground, mobileBackground, mobileBanner } = value as Record<string, unknown>;
  if (!isAssetId(desktopBackground)) return undefined;
  if (mobileBackground !== undefined && !isAssetId(mobileBackground)) return undefined;
  if (mobileBanner !== undefined && !isAssetId(mobileBanner)) return undefined;
  return {
    desktopBackground,
    ...(mobileBackground !== undefined && { mobileBackground }),
    ...(mobileBanner !== undefined && { mobileBanner }),
  };
};

const toPreviewAsset = (assetId: string) => ({ __typename: 'CustomThemeAsset' as const, id: `${PREVIEW_THEME_ID}-${assetId}`, assetURL: getTemplateArtUrl(assetId) });

/**
 * The custom theme an art template fills once applied, for previews that render before it is.
 * Mirrors design_service `mapTemplateArtToSlots`: Split View shows the mobile background from
 * `mobileThemeAsset` and has no banner; Classic Navigation puts the banner there and the mobile
 * background in its own slot (mode OWN).
 */
export const buildTemplateCustomThemePreview = (ids: TemplateArtPreviewIds, layout: DesignLayoutType): CustomThemeSlotsFragment => {
  const desktopThemeAsset = toPreviewAsset(ids.desktopBackground);
  const mobileBackground = ids.mobileBackground ? toPreviewAsset(ids.mobileBackground) : null;
  if (layout === DesignLayoutType.aloha) {
    return {
      __typename: 'CustomTheme',
      id: PREVIEW_THEME_ID,
      desktopThemeAsset,
      mobileThemeAsset: mobileBackground,
      mobileBackgroundAsset: null,
      mobileBannerEnabled: null,
      mobileBackgroundMode: CustomThemeMobileBackgroundMode.INHERIT,
    };
  }
  const banner = ids.mobileBanner ? toPreviewAsset(ids.mobileBanner) : null;
  return {
    __typename: 'CustomTheme',
    id: PREVIEW_THEME_ID,
    desktopThemeAsset,
    mobileThemeAsset: banner,
    mobileBackgroundAsset: mobileBackground,
    mobileBannerEnabled: !!banner,
    mobileBackgroundMode: mobileBackground ? CustomThemeMobileBackgroundMode.OWN : CustomThemeMobileBackgroundMode.INHERIT,
  };
};
